import React, { useState, useCallback, useEffect } from 'react';
import './LandingPage.css';
import MigrationBanner from './MigrationBanner';
import packageJson from '../../package.json';

const APP_URL = 'https://app.lawnsmartapp.com';

function track(name, params = {}) {
  if (window.gtag) window.gtag('event', name, { event_category: 'landing', ...params });
}

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
}

/* ── Nav ── */
function Nav() {
  return (
    <nav className="lp-nav">
      <a href={APP_URL} className="lp-nav-logo">
        <img src="/logo192.png" alt="Lawn.Smart" />
        <span>Lawn<span className="smart">.Smart</span></span>
      </a>
      <div className="lp-nav-links">
        <a href="#how" onClick={e => { e.preventDefault(); scrollTo('how'); }}>How it works</a>
        <a href="#months" onClick={e => { e.preventDefault(); scrollTo('months'); }}>12-month plan</a>
        <a href="#smart" onClick={e => { e.preventDefault(); scrollTo('smart'); }}>What makes it smart</a>
        <a href="#faq" onClick={e => { e.preventDefault(); scrollTo('faq'); }}>FAQ</a>
      </div>
      <a className="lp-btn lp-btn-primary" href={APP_URL} onClick={() => track('cta_click', { label: 'nav' })}>
        Open app <span className="lp-btn-arrow">→</span>
      </a>
    </nav>
  );
}

/* ── Hero phone mockup ── */
const PHONE_TASKS = [
  { done: true,  ttl: 'Apply crabgrass pre-emergent', sub: 'Before soil hits 55°F · ~May 4',   when: 'DONE'     },
  { done: true,  ttl: 'Core aerate compacted areas',  sub: 'Spring window for zone 6b',         when: 'DONE'     },
  { done: true,  ttl: 'Overseed thin areas',          sub: 'KBG/fescue blend recommended',      when: 'DONE'     },
  { done: false, ttl: 'Slow-release feed (½ rate)',   sub: 'If you overseeded, halve the N',    when: 'THIS WK'  },
  { done: false, ttl: 'Soil pH check',                sub: 'Target 6.2–7.0 for fescue',         when: 'SOON'     },
];

function PhoneMockup() {
  return (
    <div className="lp-phone-stage">
      <div className="lp-phone-bg" />
      <div className="lp-phone">
        <div className="lp-phone-screen">
          <div className="lp-phone-statusbar">
            <span>9:41</span><span>●●● 100%</span>
          </div>
          <div className="lp-phone-app-head">
            <div className="left">
              <img src="/logo192.png" alt="" />
              <span className="lp-phone-name">Lawn<span>.Smart</span></span>
            </div>
            <span className="lp-phone-zone">MA · 6b</span>
          </div>
          <div className="lp-phone-month">
            <div className="row1"><span className="m">May</span><span className="ct">3 of 8 done</span></div>
            <div className="lp-phone-bar"><i /></div>
          </div>
          <div className="lp-phone-tasks">
            {PHONE_TASKS.map((t, i) => (
              <div key={i} className={`lp-ptask${t.done ? ' done' : ''}`}>
                <div className="check" />
                <div className="pbody">
                  <div className="ttl">{t.ttl}</div>
                  <div className="sub">{t.sub}</div>
                </div>
                <span className="when">{t.when}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── ZIP card ── */
function ZipCard() {
  const [zip, setZip] = useState('01890');

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    track('zip_submit', { zip });
    window.location.href = APP_URL;
  }, [zip]);

  return (
    <div className="lp-zip-card">
      <form className="lp-zip-row" onSubmit={handleSubmit}>
        <label className="lp-zip-input">
          <span className="lp-zip-lbl">ZIP</span>
          <input
            value={zip}
            onChange={e => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
            inputMode="numeric"
            maxLength={5}
            aria-label="Enter your ZIP code"
          />
        </label>
        <button type="submit" className="lp-btn lp-btn-primary lp-btn-lg">
          See my plan <span className="lp-btn-arrow">→</span>
        </button>
      </form>
      <div className="lp-zip-result">
        <div>
          <div className="zone">Zone 6b · Massachusetts</div>
          <div className="meta">Cool-season grass · Frost risk through May 15</div>
        </div>
        <div className="chev">→</div>
      </div>
    </div>
  );
}

/* ── Hero ── */
function Hero() {
  return (
    <section className="lp-hero">
      <div className="lp-hero-grid">
        <div>
          <div className="lp-eyebrow-row">
            <span className="lp-eyebrow-pill">
              <span className="dot" />Free · No account · Works offline
            </span>
            <span className="lp-eyebrow-meta">v{packageJson.version} · 50 states</span>
          </div>
          <h1 className="lp-hero-headline">
            Know exactly what your lawn needs{' '}
            <span className="hl">this&nbsp;month</span>,{' '}
            <em>not someday.</em>
          </h1>
          <p className="lp-hero-sub">
            Tell Lawn.Smart your ZIP. We'll match you to your USDA hardiness zone
            and hand you a month-by-month checklist tuned to your grass, your soil,
            and your state's calendar.
          </p>
          <ZipCard />
          <div className="lp-hero-trust">
            <div className="lp-trust-item">
              <span className="lp-stars">★★★★★</span>{' '}
              <b>4.9</b> · 1,240+ reviews
            </div>
            <div className="lp-trust-item"><b>14,800</b> lawns planned this month</div>
            <div className="lp-trust-item"><b>All 50 states</b> supported</div>
          </div>
        </div>
        <PhoneMockup />
      </div>
    </section>
  );
}

/* ── Proof strip ── */
const PROOF = [
  { num: '50',    lab: 'States supported, each with regional timing rules' },
  { num: '13',    lab: 'USDA hardiness zones, plus subzones a/b' },
  { num: '$0',    lab: 'No account, no subscription, no upsell' },
  { num: '9 sec', lab: 'Average time from ZIP to your first plan' },
];

function ProofStrip() {
  return (
    <div className="lp-proof-strip">
      {PROOF.map((p, i) => (
        <div key={i} className="lp-proof-cell">
          <div className="num">{p.num}</div>
          <div className="lab">{p.lab}</div>
        </div>
      ))}
    </div>
  );
}

/* ── How it works ── */
function HowItWorks() {
  return (
    <section className="lp-section" id="how">
      <div className="lp-section-tag">How it works</div>
      <h2 className="lp-section-h2">Three taps to a plan that's actually yours.</h2>
      <p className="lp-section-lede">
        Most lawn advice is national, generic, or copy-pasted from a fertilizer brand.
        Lawn.Smart figures out where you are, what's growing, and what should happen
        this week — then it gets out of your way.
      </p>
      <div className="lp-steps">
        <div className="lp-step">
          <h3>Tell us your ZIP.</h3>
          <p>That's it — we look up your USDA hardiness zone, your state's climate quirks, and the typical grass species in your area.</p>
          <div className="lp-step-visual">
            <div className="lp-step1-vis">01890<span className="lp-cursor" /></div>
          </div>
        </div>
        <div className="lp-step">
          <h3>We map you to a zone.</h3>
          <p>13 zones, 26 subzones, 50 state calendars. We translate "Winchester, MA" into "cool-season turf, last frost May 15, crabgrass window mid-April."</p>
          <div className="lp-step-visual lp-step2-vis">
            <div className="lp-zone-stripes" />
            <div className="lp-zone-dot" />
          </div>
        </div>
        <div className="lp-step">
          <h3>Open the month, do the tasks.</h3>
          <p>Each month, a short, ranked checklist. Mark them off, add notes, and your history sticks around — even offline.</p>
          <div className="lp-step-visual">
            <div className="lp-step3-vis">
              {['Apply crabgrass pre-emergent','Core aerate','Slow-release feed','Soil pH check'].map((t, i) => (
                <div key={i} className="row">
                  <span className={`lp-cc${i < 2 ? ' on' : ''}`} />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Calendar strip ── */
const MONTHS = [
  { mo: 'Jan', tk: 'Rest. Sharpen mower blade.',             type: 't-rest',  tag: 'Rest'  },
  { mo: 'Feb', tk: 'Soil test kits in mail.',                type: 't-rest',  tag: 'Plan'  },
  { mo: 'Mar', tk: 'Watch soil temp. Dethatch lightly.',     type: 't-treat', tag: 'Prep'  },
  { mo: 'Apr', tk: 'Crabgrass pre-emergent. Spot-seed.',     type: 't-treat', tag: 'Treat' },
  { mo: 'May', tk: 'Overseed thin. Slow-release feed.',      type: 't-seed',  tag: 'Seed', active: true },
  { mo: 'Jun', tk: 'Raise mower height. Deep, rare watering.', type: 't-feed', tag: 'Feed' },
  { mo: 'Jul', tk: 'Hold off on fertilizer. Watch for grubs.', type: 't-rest', tag: 'Hold' },
  { mo: 'Aug', tk: 'Plan fall overseed. Order seed.',        type: 't-rest',  tag: 'Plan'  },
  { mo: 'Sep', tk: 'Best overseed window. Core aerate.',     type: 't-seed',  tag: 'Seed'  },
  { mo: 'Oct', tk: 'Heaviest feed of year. Mow shorter.',    type: 't-feed',  tag: 'Feed'  },
  { mo: 'Nov', tk: 'Final mow. Leaf cleanup.',               type: 't-treat', tag: 'Close' },
  { mo: 'Dec', tk: 'Done. Store equipment.',                 type: 't-rest',  tag: 'Rest'  },
];

function CalendarStrip() {
  return (
    <section className="lp-calendar-section" id="months">
      <div className="lp-section">
        <div className="lp-section-tag">A year at a glance</div>
        <h2 className="lp-section-h2">12 months. One coherent plan.</h2>
        <p className="lp-section-lede">
          Sample plan for Zone 6b (Boston, MA). Change your ZIP and the whole calendar reshuffles —
          Phoenix gets a totally different year than Minneapolis.
        </p>
        <div className="lp-cal-strip">
          {MONTHS.map((m, i) => (
            <div key={i} className={`lp-cal-cell ${m.type}${m.active ? ' active' : ''}`}>
              <span className="mo">{m.mo}</span>
              <div className="tk">{m.tk}</div>
              <span className="ctag">{m.tag}</span>
            </div>
          ))}
        </div>
        <div className="lp-cal-legend">
          <span><i style={{ background: '#a3d29a' }} /> Seed</span>
          <span><i style={{ background: '#d4a437' }} /> Feed</span>
          <span><i style={{ background: '#c25434' }} /> Treat</span>
          <span><i style={{ background: '#6b766f' }} /> Rest / Plan</span>
        </div>
      </div>
    </section>
  );
}

/* ── Why smart ── */
const WHY_ITEMS = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2l7 4v8l-7 4-7-4V6l7-4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><circle cx="10" cy="10" r="2.5" fill="currentColor"/></svg>
    ),
    h: 'Zone × state × grass type.',
    p: '13 USDA zones, 26 subzones, 50 state calendars, 14 common turf species. Every task respects all four.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M3 8h14M7 4V2M13 4V2" stroke="currentColor" strokeWidth="1.5"/></svg>
    ),
    h: 'Built around real calendars.',
    p: 'Crabgrass pre-emergent is timed to soil temp, not "April." Overseed windows shift by region. Frost dates inform every spring task.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 17V8l7-5 7 5v9" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M8 17v-5h4v5" stroke="currentColor" strokeWidth="1.5"/></svg>
    ),
    h: 'Yours, on every device.',
    p: 'Progressive Web App — install on iPhone, Android, or desktop. Your tasks, notes, and history work offline.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M10 6v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
    ),
    h: 'No account. No upsell. Free.',
    p: "We don't sell fertilizer, leads, or your data. The whole thing runs locally. The app is, and stays, free.",
  },
];

function WhySmart() {
  return (
    <section className="lp-section" id="smart">
      <div className="lp-why-grid">
        <div>
          <div className="lp-section-tag">What makes it smart</div>
          <h2 className="lp-section-h2">Generic lawn advice is why your lawn looks generic.</h2>
          <p className="lp-section-lede">
            Most apps and articles assume your lawn is in Ohio. Lawn.Smart adjusts every recommendation
            to where you actually live.
          </p>
          <div className="lp-why-list">
            {WHY_ITEMS.map((item, i) => (
              <div key={i} className="lp-why-item">
                <div className="lp-why-ic">{item.icon}</div>
                <div>
                  <h4>{item.h}</h4>
                  <p>{item.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="lp-why-visual">
          <div className="lp-why-stripes" />
          <div className="lp-map-overlay" />
          <div className="lp-map-pin" style={{ top: '32%', left: '62%' }} />
          <div className="lp-map-pin" style={{ top: '54%', left: '24%', background: '#4ade80', boxShadow: '0 0 0 4px rgba(74,222,128,.3)' }} />
          <div className="lp-map-pin" style={{ top: '68%', left: '48%', background: '#fff', boxShadow: '0 0 0 4px rgba(255,255,255,.25)' }} />
          <div className="lp-why-placeholder">
            <b>USDA hardiness map<br />with your zone highlighted</b>
            <span>[ swap with real map asset ]</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Screens row ── */
function ScreensRow() {
  return (
    <div className="lp-screens-outer">
      <div className="lp-section-tag">Same app, every climate</div>
      <h2 className="lp-section-h2">A May plan in Boston is nothing like a May plan in Phoenix.</h2>
      <p className="lp-section-lede">
        Three real users, three regions, three completely different task lists for the exact same week.
      </p>
      <div className="lp-screens-row">
        <div className="lp-mini-phone">
          <div className="lp-mini-screen">
            <span className="ms-loc">Boston · Zone 6b</span>
            <div className="ms-ttl">May plan: <em>seed &amp; feed</em></div>
            <span className="ms-tag">Cool-season · KBG/Fescue</span>
            <div className="ms-rows">
              <div className="ms-r"><div className="b" />Overseed thin areas</div>
              <div className="ms-r by"><div className="b" />Slow-release N (½ rate)</div>
              <div className="ms-r"><div className="b" />Soil pH test</div>
              <div className="ms-r br"><div className="b" />Watch for crabgrass breakthrough</div>
            </div>
            <div className="ms-footer"><span>3/8 done</span><span>→</span></div>
          </div>
        </div>
        <div className="lp-mini-phone">
          <div className="lp-mini-screen">
            <span className="ms-loc">Phoenix · Zone 9b</span>
            <div className="ms-ttl">May plan: <em>transition</em></div>
            <span className="ms-tag">Warm-season · Bermuda</span>
            <div className="ms-rows">
              <div className="ms-r"><div className="b" />Lower mowing height to 1″</div>
              <div className="ms-r by"><div className="b" />Heaviest fertilization window</div>
              <div className="ms-r"><div className="b" />Switch to deep, infrequent water</div>
              <div className="ms-r"><div className="b" />Edge beds &amp; sidewalks</div>
            </div>
            <div className="ms-footer"><span>1/6 done</span><span>→</span></div>
          </div>
        </div>
        <div className="lp-mini-phone">
          <div className="lp-mini-screen">
            <span className="ms-loc">Atlanta · Zone 8a</span>
            <div className="ms-ttl">May plan: <em>wake-up</em></div>
            <span className="ms-tag">Warm/cool mix · Zoysia/Fescue</span>
            <div className="ms-rows">
              <div className="ms-r by"><div className="b" />Pre-emergent reapplication</div>
              <div className="ms-r"><div className="b" />Begin weekly mow</div>
              <div className="ms-r"><div className="b" />Spot-treat broadleaf weeds</div>
              <div className="ms-r"><div className="b" />Inspect for brown patch</div>
            </div>
            <div className="ms-footer"><span>0/5 done</span><span>→</span></div>
          </div>
        </div>
      </div>
      <div className="lp-screens-caption">
        <div><b>Cool-season North.</b> Spring overseed window, half-rate feed, pre-emergent timing tied to soil temp.</div>
        <div><b>Warm-season Southwest.</b> Bermudagrass wakes up — heaviest fert window of the year, watering shifts.</div>
        <div><b>Transition zone.</b> Mixed species, two calendars overlaid. The trickiest region — and where Lawn.Smart matters most.</div>
      </div>
    </div>
  );
}

/* ── Testimonials ── */
const QUOTES = [
  { q: '"It\'s the first thing I\'ve found that actually understands my soil temps. The crabgrass timing alone was worth the install."', initials: 'MR', name: 'Mike R.', loc: 'Boston, MA · Zone 6b · since 2024' },
  { q: '"My lawn finally stopped looking like the neighbor\'s tax-form printout. The monthly checklist is small enough that I do it."', initials: 'SP', name: 'Sarah P.', loc: 'Austin, TX · Zone 8b · since 2025' },
  { q: '"No account, no upsell, just a list of three things to do this week. Exactly what I wanted, nothing I didn\'t."', initials: 'JK', name: 'Jamie K.', loc: 'Boise, ID · Zone 7a · since 2025' },
];

function Testimonials() {
  return (
    <section className="lp-section">
      <div className="lp-section-tag">From the lawns</div>
      <h2 className="lp-section-h2">People who used to Google "when to seed Kentucky bluegrass."</h2>
      <div className="lp-quotes">
        {QUOTES.map((q, i) => (
          <div key={i} className="lp-quote">
            <div className="lp-stars">★★★★★</div>
            <p>{q.q}</p>
            <div className="by">
              <div className="av">{q.initials}</div>
              <div className="who">
                <b>{q.name}</b>
                <span>{q.loc}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── FAQ ── */
const FAQS = [
  { q: 'Is it really free?',                                          a: 'Yes. No subscription, no in-app purchases, no email required. The whole thing runs on your device.' },
  { q: 'How is this different from Sunday, Scotts, or a Reddit thread?', a: "We don't sell fertilizer or leads — so we can give you neutral timing advice tied to your zone, not a product calendar. Reddit is great, but a Reddit thread is not a plan." },
  { q: 'What grass types do you support?',                            a: 'Kentucky bluegrass, tall fescue, fine fescue, perennial rye, Bermuda, zoysia, St. Augustine, centipede, buffalo, and bahiagrass. We\'ll add more — request via the app.' },
  { q: 'Where does the timing data come from?',                       a: 'USDA hardiness zones, NOAA frost-date averages, state extension services (UMass, A&M, UF/IFAS, etc.), and soil-temp models. Sources are linked inside each task.' },
  { q: 'Does it work offline?',                                       a: 'Yes — install to your home screen and the app keeps working without service. Your tasks, notes, and history persist locally.' },
  { q: 'Who made this?',                                              a: 'One person, in their own backyard, after one too many years of guessing. A long-term commitment to keeping it free.' },
];

function FAQ() {
  return (
    <section className="lp-section" id="faq">
      <div className="lp-faq-grid">
        <div>
          <div className="lp-section-tag">Common questions</div>
          <h2 className="lp-section-h2">Things people ask before they install.</h2>
          <p className="lp-section-lede" style={{ marginBottom: 0 }}>
            If you're wondering whether this is yet another fertilizer ad, it isn't.
          </p>
        </div>
        <div className="lp-faq-list">
          {FAQS.map((f, i) => (
            <details key={i} className="lp-faq" open={i === 0 || undefined}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Final CTA ── */
function FinalCTA() {
  return (
    <section className="lp-final-cta">
      <h2>Your lawn doesn't need more product.<br /><em>It needs a plan.</em></h2>
      <p>Free forever. Works offline. Tuned to where you live. Open Lawn.Smart and have your May checklist in nine seconds.</p>
      <div className="btns">
        <a className="lp-btn lp-btn-honey lp-btn-lg" href={APP_URL} onClick={() => track('cta_click', { label: 'final_cta' })}>
          Open Lawn.Smart <span className="lp-btn-arrow">→</span>
        </a>
        <button
          className="lp-btn lp-btn-ghost-dark lp-btn-lg"
          onClick={() => scrollTo('months')}
        >
          See a sample plan first
        </button>
      </div>
      <div className="micro">No account · No subscription · No emails</div>
    </section>
  );
}

/* ── Footer ── */
function Footer() {
  return (
    <footer className="lp-footer">
      <div className="lp-f-grid">
        <div>
          <a href={APP_URL} className="lp-footer-logo">
            <img src="/logo192.png" alt="Lawn.Smart" />
            <span>Lawn<span className="smart">.Smart</span></span>
          </a>
          <p className="lp-footer-tagline">
            A free, zone-intelligent lawn-care planner. Made for homeowners who'd rather
            spend Sunday outside than reading fertilizer labels.
          </p>
        </div>
        <div>
          <h5>Product</h5>
          <ul>
            <li><a href={APP_URL}>Open app</a></li>
            <li><a href="#how" onClick={e => { e.preventDefault(); scrollTo('how'); }}>How it works</a></li>
            <li><a href="#months" onClick={e => { e.preventDefault(); scrollTo('months'); }}>12-month plan</a></li>
          </ul>
        </div>
        <div>
          <h5>By state</h5>
          <ul>
            <li><a href={APP_URL}>California</a></li>
            <li><a href={APP_URL}>Texas</a></li>
            <li><a href={APP_URL}>Florida</a></li>
            <li><a href={APP_URL}>All 50 →</a></li>
          </ul>
        </div>
        <div>
          <h5>About</h5>
          <ul>
            <li><a href={APP_URL}>Sources</a></li>
            <li><a href={APP_URL}>Privacy</a></li>
            <li><a href={APP_URL}>Contact</a></li>
          </ul>
        </div>
      </div>
      <div className="lp-f-bottom">
        <span>Lawn.Smart v{packageJson.version} · © {new Date().getFullYear()}</span>
        <span>app.lawnsmartapp.com</span>
      </div>
    </footer>
  );
}

/* ── Scroll to top ── */
function ScrollToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      className="lp-scroll-top"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
    >
      ↑
    </button>
  );
}

/* ── Root ── */
export default function LandingPage() {
  return (
    <div className="lp">
      <Nav />
      <Hero />
      <ProofStrip />
      <HowItWorks />
      <CalendarStrip />
      <WhySmart />
      <div style={{ padding: '0 0 96px' }}>
        <ScreensRow />
      </div>
      <Testimonials />
      <FAQ />
      <FinalCTA />
      <Footer />
      {/* Straggler migration catch — only shows if www localStorage has data */}
      <MigrationBanner />
      <ScrollToTop />
    </div>
  );
}
